// State adapters for JobModal — kept separate so the modal stays
// under the 150-line cap. Maps the wire DTO to/from the React form
// state, including null → empty-string coercion for date inputs.
import type {
  JobCreateInputT,
  JobDto,
  JobUpdateInputT,
} from "../../../../../../shared/types/production-schedule";
import { isoToday, addDays } from "../lib/dates";
import type { JobFormState } from "./JobFormFields";

export function emptyForm(): JobFormState {
  return {
    jobNumber: "",
    name: "",
    customer: "",
    contact: "",
    location: "",
    notes: "",
    solutionTypeId: null,
    status: "New",
    priority: "Normal",
    assigneeId: null,
    startDate: isoToday(),
    dueDate: addDays(isoToday(), 14),
    sharepointUrl: "",
    orderValue: "",
    nominalStageHours: {},
    simpleMode: false,
  };
}

export function fromJob(j: JobDto): JobFormState {
  return {
    jobNumber: j.jobNumber,
    name: j.name,
    customer: j.customer ?? "",
    contact: j.contact ?? "",
    location: j.location ?? "",
    notes: j.notes ?? "",
    solutionTypeId: j.solutionType?.id ?? null,
    status: j.status,
    priority: j.priority,
    assigneeId: j.assignee?.id ?? null,
    startDate: j.startDate ?? "",
    dueDate: j.dueDate ?? "",
    sharepointUrl: j.sharepointUrl ?? "",
    orderValue: j.orderValue !== null ? String(j.orderValue) : "",
    nominalStageHours: { ...(j.nominalStageHours ?? {}) },
    simpleMode: j.simpleMode,
  };
}

export function toCreatePayload(s: JobFormState): JobCreateInputT {
  return {
    jobNumber: s.jobNumber || undefined,
    name: s.name,
    customer: s.customer || null,
    contact: s.contact || null,
    location: s.location || null,
    notes: s.notes || null,
    solutionTypeId: s.solutionTypeId,
    status: s.status,
    priority: s.priority,
    assigneeId: s.assigneeId,
    startDate: s.startDate || null,
    dueDate: s.dueDate || null,
    sharepointUrl: s.sharepointUrl?.trim() ? s.sharepointUrl.trim() : null,
    orderValue: parseOrderValue(s.orderValue),
    nominalStageHours: s.nominalStageHours ?? {},
    simpleMode: s.simpleMode,
  };
}

// Empty string → null (clear the field); otherwise parse the number
// and reject NaN so a typo doesn't end up as 0 silently.
function parseOrderValue(raw: string): number | null {
  if (!raw || !raw.trim()) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function toUpdatePayload(s: JobFormState): JobUpdateInputT {
  return toCreatePayload(s);
}
